import type { DateFormat } from './DateCell.types';

/**
 * Parsea cualquier valor de fecha a un objeto Date válido
 * Soporta: Date, string ISO, timestamp, y formatos comunes
 */
export const parseDate = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  // Si ya es un Date
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  // Si es un número (timestamp)
  if (typeof value === 'number') {
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  // Si es un string
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    // Intentar parsear directamente (ISO, etc.)
    let date = new Date(trimmed);
    if (!isNaN(date.getTime())) return date;

    // Formato dd/MM/yyyy o dd-MM-yyyy
    const ddmmyyyy = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (ddmmyyyy) {
      const [, day, month, year] = ddmmyyyy;
      date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (!isNaN(date.getTime())) return date;
    }

    // Formato MM/dd/yyyy o MM-dd-yyyy (US format)
    const mmddyyyy = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (mmddyyyy) {
      const [, month, day, year] = mmddyyyy;
      date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (!isNaN(date.getTime())) return date;
    }

    // Formato yyyy/MM/dd o yyyy-MM-dd
    const yyyymmdd = trimmed.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/);
    if (yyyymmdd) {
      const [, year, month, day] = yyyymmdd;
      date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (!isNaN(date.getTime())) return date;
    }

    return null;
  }

  return null;
};

/**
 * Calcula el tiempo relativo (hace X días, ayer, hoy, etc.)
 */
const getRelativeTime = (date: Date, locale: string = 'es-ES'): string => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const diffTime = target.getTime() - today.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  // Traducciones
  const translations: Record<string, Record<string, string>> = {
    'es-ES': {
      today: 'Hoy',
      yesterday: 'Ayer',
      tomorrow: 'Mañana',
      daysAgo: 'hace {n} días',
      inDays: 'en {n} días',
      weeksAgo: 'hace {n} semanas',
      inWeeks: 'en {n} semanas',
      monthsAgo: 'hace {n} meses',
      inMonths: 'en {n} meses',
      yearsAgo: 'hace {n} años',
      inYears: 'en {n} años',
    },
    'en-US': {
      today: 'Today',
      yesterday: 'Yesterday',
      tomorrow: 'Tomorrow',
      daysAgo: '{n} days ago',
      inDays: 'in {n} days',
      weeksAgo: '{n} weeks ago',
      inWeeks: 'in {n} weeks',
      monthsAgo: '{n} months ago',
      inMonths: 'in {n} months',
      yearsAgo: '{n} years ago',
      inYears: 'in {n} years',
    },
  };

  const t = translations[locale] || translations['es-ES'];

  if (diffDays === 0) return t.today;
  if (diffDays === -1) return t.yesterday;
  if (diffDays === 1) return t.tomorrow;

  const absDays = Math.abs(diffDays);

  if (absDays < 7) {
    return diffDays < 0
      ? t.daysAgo.replace('{n}', absDays.toString())
      : t.inDays.replace('{n}', absDays.toString());
  }

  if (absDays < 30) {
    const weeks = Math.round(absDays / 7);
    return diffDays < 0
      ? t.weeksAgo.replace('{n}', weeks.toString())
      : t.inWeeks.replace('{n}', weeks.toString());
  }

  if (absDays < 365) {
    const months = Math.round(absDays / 30);
    return diffDays < 0
      ? t.monthsAgo.replace('{n}', months.toString())
      : t.inMonths.replace('{n}', months.toString());
  }

  const years = Math.round(absDays / 365);
  return diffDays < 0
    ? t.yearsAgo.replace('{n}', years.toString())
    : t.inYears.replace('{n}', years.toString());
};

/**
 * Formatea una fecha según el formato especificado
 */
export const formatDate = (
  date: Date,
  format: DateFormat = 'short',
  locale: string = 'es-ES'
): string => {
  const day = date.getDate();
  const month = date.getMonth();
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  switch (format) {
    case 'short':
      // 14/12/2025
      return `${day.toString().padStart(2, '0')}/${(month + 1).toString().padStart(2, '0')}/${year}`;

    case 'long':
      // 14 de diciembre de 2025
      return date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });

    case 'medium':
      // 14 dic 2025
      return date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });

    case 'iso':
      // 2025-12-14
      return `${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;

    case 'datetime-short':
      // 14/12/2025 14:30
      return `${day.toString().padStart(2, '0')}/${(month + 1).toString().padStart(2, '0')}/${year} ${hours}:${minutes}`;

    case 'datetime-long':
      // 14 de diciembre de 2025 14:30
      return date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

    case 'time':
      // 14:30
      return `${hours}:${minutes}`;

    case 'relative':
      return getRelativeTime(date, locale);

    case 'month-year':
      // diciembre 2025
      return date.toLocaleDateString(locale, {
        month: 'long',
        year: 'numeric',
      });

    case 'day-month':
      // 14 de diciembre
      return date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
      });

    default:
      return `${day.toString().padStart(2, '0')}/${(month + 1).toString().padStart(2, '0')}/${year}`;
  }
};
